import React from "react";
import { ScheduleRow, ScheduleWeek } from "../../utils/parseScheduleExcel";

interface UploadSummaryCardProps {
  fileName: string;
  rows: ScheduleRow[];
  weeks: ScheduleWeek[];
  isFetchingEmployees: boolean;
  onReset: () => void;
  onProceed: () => void;
}

const SummaryBadge: React.FC<{ label: string; value: string | number; variant?: "blue" | "purple" | "gray" }> = ({
  label,
  value,
  variant = "gray",
}) => {
  const colors = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    gray: "bg-gray-50 border-gray-200 text-gray-600",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${colors[variant]}`}>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-bold">{value}</p>
    </div>
  );
};

const UploadSummaryCard: React.FC<UploadSummaryCardProps> = ({
  fileName,
  rows,
  weeks,
  isFetchingEmployees,
  onReset,
  onProceed,
}) => {
  return (
    <div className="mt-4 rounded-xl border border-green-200 bg-white p-5 shadow-sm">
      <p className="mb-3 text-sm font-bold text-gray-700">Parsed Summary</p>
      <hr className="mb-3 border-gray-100" />

      {/* Stats row */}
      <div className="flex flex-wrap gap-2 mb-3">
        <SummaryBadge label="Employees" value={rows.length} variant="blue" />
        <SummaryBadge label="Weeks" value={weeks.length} variant="purple" />
        {weeks.map((w, i) => (
          <SummaryBadge key={i} label={`Week ${i + 1}`} value={w.weekLabel} variant="gray" />
        ))}
      </div>

      {/* Employee fetch indicator */}
      {isFetchingEmployees && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-sm text-blue-600">
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Matching employees with system records…
        </div>
      )}

      {/* Employee chips */}
      <div className="max-h-40 overflow-y-auto">
        <p className="mb-1.5 text-xs font-semibold text-gray-400">Employees in this schedule:</p>
        <div className="flex flex-wrap gap-1.5">
          {rows.map((row) => (
            <span
              key={row.id}
              className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600"
            >
              {row.employee}
            </span>
          ))}
        </div>
      </div>

      <hr className="my-3 border-gray-100" />

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          onClick={onReset}
        >
          Upload Different File
        </button>
        <button
          type="button"
          disabled={isFetchingEmployees}
          className="flex items-center gap-2 rounded-full bg-blue-600 px-6 py-2 text-sm font-bold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          onClick={onProceed}
        >
          Review Schedule
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default UploadSummaryCard;
